import { z } from 'zod';
import { MoneyWireSchema } from './money.js';
import { StartDateConstraintsSchema } from './sign.js';
/**
 * Personalization — "your estimated bill" (E-SF04) — the shapes for
 * `GET /bff/estimate` (the estimate the screen renders) and
 * `POST /bff/quote/draft` (the DRAFT `web_self_serve` quote the funnel creates so
 * sign has a `quoteId`).
 *
 * ── WHY THESE LIVE IN THE CONTRACT ─────────────────────────────────────────
 * They are the one edge between the SPA and the BFF (ADR-0002 §4). The BFF uses
 * them as its `response` schema (so the boot-time response-schema guard walks
 * them and the handler validates its own output); US-Shop imports them for typed
 * fetchers. Neither declares `tenantId`, a credential, PII, or the resolved
 * ESIID/quoteId — the browser holds only the opaque session-scoped `candidateId`
 * (AC3 non-enumerability), and the DRAFT quote's id stays server-side.
 *
 * ── AC2 — NEVER A GUARANTEED PRICE ─────────────────────────────────────────
 * The estimate response carries `isEstimate: literal(true)`, so "guaranteed
 * price" is un-representable on the wire, and the screen always renders the
 * disclaimer copy. The numbers are minor-unit integers (money) / integer
 * micro-cent rate strings — never floats, no client-side money math.
 */
// ─────────────────────────────────────────────────────────────────────────────
// GET /bff/estimate — the personalization estimate
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Home-size profile (AC1 fallback). When 365-day history is unavailable
 * (move-in / new construction, or a switch-in premise not on our REP), the
 * estimate falls back to a home-size profile — reusing the E-SF02 needs-quiz
 * usage size (`small`/`medium`/`large`) when the visitor answered it. The BFF
 * maps each size to a representative monthly usage server-side; the browser
 * never sends a kWh number it could use to fabricate a bill.
 */
export const HomeProfileSize = z.enum(['small', 'medium', 'large']);
/**
 * ── THE SHOPPER'S DECLARED MONTHLY USAGE (E-SF19 P0a) ──────────────────────
 *
 * The storefront's consumption step collects a PRECISE monthly kWh figure (a
 * tier's representative usage, or a typed Custom value). Until E-SF19 that
 * number was thrown away at the network edge: the only usage the browser could
 * state was a THREE-MEMBER home-size token, so a shopper declaring 120 kWh/mo
 * was priced at 500 and shown a bill ~180% over, under the caption "we couldn't
 * find usage history for this address" — which was not what happened. Their own
 * figure had been discarded.
 *
 * So the declared figure now travels, and it travels BOUNDED. The bounds are the
 * whole reason a raw kWh is safe to accept where a token was chosen before:
 * `POST /bff/credit/check` sizes the PUCT §25.478 deposit from the SAME projected
 * bill, so an unbounded client-supplied usage would be an unbounded client-supplied
 * input to a regulated charge. Within these bounds it is a plausible RESIDENTIAL
 * statement, and it replaces a GUESS (the medium-home default), not a measurement:
 * a real GMO usage read still wins wherever one exists.
 *
 * ⚠️ The floor is deliberately NOT zero. A near-zero declaration would drive the
 * projected bill — and therefore the deposit ceiling — toward nothing, which is the
 * one direction a customer benefits from lying in. 50 kWh/mo is below any occupied
 * Texas residence and still refuses the degenerate case.
 */
export const DECLARED_MONTHLY_KWH_MIN = 50;
export const DECLARED_MONTHLY_KWH_MAX = 10_000;
/** A shopper-declared monthly usage (kWh), bounded to a plausible residential range. */
export const DeclaredMonthlyKwhSchema = z
    .number()
    .int()
    .min(DECLARED_MONTHLY_KWH_MIN)
    .max(DECLARED_MONTHLY_KWH_MAX);
/**
 * Where the estimate's usage basis came from.
 *
 *   `declared` — the shopper told us, on the consumption step (E-SF19).
 *   `history`  — a ~365-day GMO usage read for this premise was averaged.
 *   `profile`  — neither was available, so the home-size fallback was used (AC1).
 *
 * The screen names the one that was actually used. It must never explain a
 * `declared` estimate with the `profile` sentence — inventing a reason is the
 * defect E-SF19 exists to close.
 */
export const EstimateUsageSource = z.enum(['declared', 'history', 'profile']);
/**
 * How the estimate's monthly usage was derived. `source: 'declared'` means the
 * shopper's own figure was used; `source: 'history'` means a ~365-day GMO usage
 * read was averaged; `source: 'profile'` means the home-size fallback was used
 * (AC1) and `profileSize` names which profile.
 */
export const EstimateUsageBasisSchema = z.object({
    source: EstimateUsageSource,
    /** The monthly usage (kWh) the estimate was priced at. */
    monthlyKwh: z.number().int().positive(),
    /** Present only when `source === 'profile'` — which home-size profile was used. */
    profileSize: HomeProfileSize.optional(),
    /**
     * True when `monthlyKwh` sits OUTSIDE the three published §25.475 reference
     * usages (below 500 or above 2000 kWh/mo), so the bill was projected by
     * extending the plan's published curve rather than read off it.
     *
     * It exists so the screen can SAY that. The alternative the storefront used to
     * ship — silently clamping to the nearest published point — is what turned a
     * 120 kWh declaration into a 500 kWh bill with no indication that anything had
     * been substituted.
     */
    outsideReferenceRange: z.boolean(),
});
/**
 * One point on the effective-rate curve — the estimated all-in ¢/kWh at one of
 * the three PUCT §25.475 reference usages (500 / 1000 / 2000 kWh).
 *
 * `effectiveRateMinor` is the effective rate expressed as **¢/kWh × 10⁴** (an
 * integer string — micro-cents; divide by 10⁴ for ¢/kWh), matching Pricing's own
 * `ratePerKwhMinor` scale. It is a RATE, not money — so it rides as a scaled
 * integer string, never a float and never a `*_minor` money field.
 */
export const EstimateRateTierSchema = z.object({
    usageKwh: z.number().int().positive(),
    /** The estimated monthly bill at this reference usage (versioned money). */
    monthlyBill: MoneyWireSchema,
    /** Effective ¢/kWh × 10⁴ at this usage, as an integer string. */
    effectiveRateMinor: z
        .string()
        .regex(/^\d+$/u, 'effectiveRateMinor must be a non-negative integer'),
});
/**
 * `GET /bff/estimate` response — the personalization estimate the screen renders.
 *
 * `estimatedMonthlyCost` is the bill at the premise's actual monthly usage
 * (interpolated across the reference curve, server-side, in exact integer minor
 * units). `effectiveRateAtUsageMinor` is the effective rate at that same usage.
 * `curve` is the three-point effective-rate curve (500/1000/2000) the screen
 * plots the premise's usage against.
 */
export const EstimateResponseSchema = z.object({
    usage: EstimateUsageBasisSchema,
    /** The estimated monthly cost at the premise's actual usage (versioned money). */
    estimatedMonthlyCost: MoneyWireSchema,
    /** The effective ¢/kWh × 10⁴ at the premise's actual usage (integer string). */
    effectiveRateAtUsageMinor: z
        .string()
        .regex(/^\d+$/u, 'effectiveRateAtUsageMinor must be a non-negative integer'),
    /** The three §25.475 reference points (500/1000/2000 kWh), ordered by usage. */
    curve: z.array(EstimateRateTierSchema).length(3),
    /**
     * AC2 — this is an ESTIMATE, never a guaranteed price. `literal(true)` makes a
     * "guaranteed" estimate un-representable; the screen always renders the
     * disclaimer.
     */
    isEstimate: z.literal(true),
    /**
     * The supply-start window, so the estimate step can ASK for the start date
     * BEFORE the DRAFT quote is created.
     *
     * ⚠️ WHY HERE AND NOT ONLY ON THE SIGN CONTEXT (2026-09-18). The sign step
     * asked for this date too, and the answer went nowhere: the DRAFT is created
     * by "Continue to sign" with a start date of "next business day", and by the
     * time the customer picks one the quote is priced, its EFL is generated and
     * the accept path is deliberately FROZEN — so a date chosen at sign could not
     * be honoured without re-pricing a signed document. Asking one step earlier
     * makes the customer's date the date the quote is built from, and the contract
     * PDF then renders it because it IS the quote's `requestedStartDate`.
     *
     * Same shape and same server-side computation as the sign context's
     * `startDate`, so one window governs both screens.
     */
    /**
     * ⚠️ OPTIONAL, AND THAT IS A DEPLOYMENT PROPERTY, NOT INDECISION (2026-09-21).
     *
     * US-Shop and US-Enrollment-BFF are SEPARATE deployments
     * (`Global-Rheora-Frontend-US-Shop` and `Global-Frontend-US-Enrollment-BFF`),
     * so the browser can be running a newer bundle than the BFF answering it.
     * Required, this field made that ordinary skew fatal: an older BFF omits it,
     * the whole `EstimateResponse` fails to parse, and the customer gets a broken
     * estimate screen instead of a missing picker — a strictly worse outcome than
     * the feature simply not being there yet.
     *
     * Absent ⇒ the estimate step does not ask for a start date and the BFF's
     * next-business-day default stands, exactly as before this feature existed.
     * The step renders and the funnel completes either way.
     */
    startDate: StartDateConstraintsSchema.optional(),
});
/**
 * The `GET /bff/estimate` inputs the FE sends as query parameters. Not a body
 * (the estimate is a read-only query): `candidate` is the opaque E-SF03 handle,
 * `plan` the shopped tariff, and `profile` the OPTIONAL E-SF02 needs-quiz size
 * used only for the AC1 fallback. Defined here so the FE fetcher builds the query
 * string from a typed shape.
 *
 * `declaredMonthlyKwh` is the shopper's own figure from the consumption step
 * (E-SF19). Like `homeProfile` it is WRITTEN THROUGH to the funnel session rather
 * than honoured for this render only — otherwise the estimate screen and the
 * §25.478 deposit could be derived from two different usages again.
 */
export const EstimateRequestSchema = z.object({
    candidateId: z.string().min(1),
    planId: z.string().min(1).max(255),
    homeProfile: HomeProfileSize.optional(),
    declaredMonthlyKwh: DeclaredMonthlyKwhSchema.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// POST /bff/funnel/usage-profile — PERSIST the shopper's chosen home size
// ─────────────────────────────────────────────────────────────────────────────
/**
 * `POST /bff/funnel/usage-profile` — record the shopper's chosen home-size
 * profile ON THE FUNNEL SESSION, at the moment they choose it.
 *
 * ── THE BUG THIS EXISTS TO CLOSE (QA-2026-08-05, BLOCKER 1) ────────────────
 * The home size was a QUERY PARAMETER on `GET /bff/estimate` and nothing else.
 * It was never persisted anywhere, so `POST /bff/credit/check` — which sizes the
 * §25.478 deposit off the SAME projected bill — had no way to learn it and every
 * customer fell through to the `medium` (1000 kWh) default. The visible symptom
 * was every persona being quoted an identical deposit, and a small-home shopper
 * reading one number on screen while being sized off another.
 *
 * The funnel can reach the credit step BEFORE the estimate step, so a cached
 * estimate could not be relied on either. The size therefore lives on the funnel
 * session, written once when it is chosen, and BOTH surfaces read it from there —
 * which is what makes "the deposit ceiling equals the estimated bill you were
 * shown" true by construction rather than by coincidence.
 *
 * ⚠️ THE SIZE TOKEN IS NO LONGER THE ONLY THING THAT TRAVELS (E-SF19 P0a).
 * This file used to state flatly that "the browser sends a SIZE TOKEN, never a
 * kWh figure … so the client cannot fabricate the usage basis a regulated deposit
 * is derived from". That reasoning held right up to the point where it produced a
 * customer-facing falsehood: a shopper who typed 120 kWh/mo was priced at the
 * `small` profile's 500 and told we could not find usage history for their
 * address. The token was not protecting the deposit so much as discarding the
 * only honest input we had.
 *
 * The trade is now explicit rather than implied. `declaredMonthlyKwh` travels,
 * BOUNDED to [DECLARED_MONTHLY_KWH_MIN, DECLARED_MONTHLY_KWH_MAX] by the schema,
 * and the server still prefers a real GMO usage read over it wherever one exists.
 * The client therefore selects a usage basis from a plausible residential range
 * only where the alternative was a guess — which is strictly better information,
 * not a new capability to fabricate one. `homeProfile` remains REQUIRED so a
 * client that cannot supply a figure still names a documented fallback.
 */
export const UsageProfileRequestSchema = z.object({
    homeProfile: HomeProfileSize,
    /** The shopper's own monthly kWh from the consumption step (E-SF19). */
    declaredMonthlyKwh: DeclaredMonthlyKwhSchema.optional(),
});
/**
 * `POST /bff/funnel/usage-profile` result — the usage basis now held on the
 * session. Echoing it back is safe (it is the caller's own choice, about their own
 * session, and carries no PII, no tenant and no upstream identifier) and it lets
 * the SPA assert the server agrees with what it just sent.
 */
export const UsageProfileResponseSchema = z.object({
    homeProfile: HomeProfileSize,
    /** The declared figure the session now holds; absent when none was supplied. */
    declaredMonthlyKwh: DeclaredMonthlyKwhSchema.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// POST /bff/quote/draft — create the DRAFT web_self_serve quote (blocker 3)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * `POST /bff/quote/draft` request — create (idempotently, per funnel session) the
 * DRAFT `web_self_serve` Quotation quote so the sign step has a `quoteId` to
 * accept. `candidateId` is the opaque premise handle; `planId` the shopped
 * tariff. Everything else the quote needs (ERCOT zone/TDSP, incumbent REP DUNS,
 * prospectUid, customer contact/name, service address) is read SERVER-SIDE from
 * the funnel session — never re-sent by the browser (AC3).
 */
export const DraftQuoteRequestSchema = z.object({
    candidateId: z.string().min(1),
    planId: z.string().min(1).max(255),
    /**
     * E-SF16 — "yes, continue at the current price".
     *
     * ONLY meaningful on the OFFER path, where this step repoints the agent's quote
     * via `select-plan` instead of creating a new DRAFT. `select-plan` re-prices
     * live and refuses with a 409 when the result differs from what the agent
     * offered; the BFF answers `offerPriceChanged`, stashes the upstream's
     * confirmation token on the funnel session, and the shopper's "continue at the
     * current price" click replays this request with the flag set.
     *
     * ⚠️ IT IS A BARE BOOLEAN, AND THAT IS DELIBERATE. The upstream's
     * `repricedSnapshotId` never reaches the browser (see `PROBLEM_TYPES
     * .offerPriceChanged`), so a client cannot confirm a price change the server did
     * not just report to THIS session: the flag only unlocks a snapshot the BFF
     * already holds, and it is inert on the non-offer path.
     *
     * Ignored (not refused) when there is nothing pending — a stale replay of a
     * confirmation must not fail a request that would otherwise succeed.
     */
    confirmPriceChange: z.boolean().optional(),
    /**
     * The supply start date the customer picked on the estimate step, `YYYY-MM-DD`
     * (2026-09-18).
     *
     * THIS IS THE DATE THE QUOTE IS BUILT FROM. The BFF passes it to Quotation's
     * create-draft as `requestedStartDate`, so the EFL, the contract term and the
     * contract PDF are all computed for it. Previously the BFF always sent "next
     * business day" and the date the customer chose at the SIGN step was dropped
     * on the floor (see `SIGNATURE_EVENT_FIELD_DISPOSITION.startDate`).
     *
     * Optional, and the BFF still falls back to the next business day when it is
     * absent: the offer path repoints an agent's existing quote rather than
     * creating one, and an older client must not start failing. The BFF validates
     * it against the same window it published on the estimate response — a date
     * outside that window is refused, never silently clamped.
     */
    requestedStartDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/u, 'requestedStartDate must be YYYY-MM-DD')
        .optional(),
});
/**
 * `POST /bff/quote/draft` result. Deliberately minimal: the resulting `quoteId`
 * is a server-side handle stored in the funnel session for the sign saga to read
 * — it is NEVER returned to the browser (AC3: nothing enumerable egresses). The
 * FE only needs to know the DRAFT exists so it can proceed to sign.
 */
export const DraftQuoteResultSchema = z.object({
    status: z.literal('created'),
});
//# sourceMappingURL=estimate.js.map